import { RadialRing } from '../systems/spawning/patterns/RadialRing.js';
import { HomingShardVolley } from '../systems/spawning/patterns/HomingShardVolley.js';
import { SweepingLaser } from '../systems/spawning/patterns/SweepingLaser.js';
import { BulletBoardGrid } from '../systems/spawning/patterns/BulletBoardGrid.js';
import { Implosion } from '../systems/spawning/patterns/Implosion.js';
import { Ostinato } from '../systems/spawning/patterns/Ostinato.js';
import { Understudy } from '../systems/spawning/patterns/Understudy.js';
import { Composition } from '../systems/spawning/patterns/Composition.js';
import { EveryoneAtOnce } from '../systems/spawning/patterns/EveryoneAtOnce.js';
import { TheLastVerse } from '../systems/spawning/patterns/TheLastVerse.js';
import { WardingCores } from '../systems/spawning/patterns/WardingCores.js';

/**
 * Act III — The Anthem (see ACT3-BIBLE.md). The chord that ran Melodia came
 * from above and was imposed. The Undersong came from below and was never a
 * song at all. The third song came from *you* — a private phrase carried up out
 * of the dark by one person with no authority to start a world over. It was
 * picked up anyway, and it has stopped needing you in order to continue.
 *
 * The antagonist principle is **authorship**, and the shared texture is that
 * **the arena is made out of the player**: your inputs, your movement history,
 * your build, your choices are the ammunition. Nothing down this act hates you;
 * everything down it is derived from you, which is worse — it argues with your
 * habits rather than your reflexes.
 *
 * Same def shape as data/bosses.js and data/act2Bosses.js, so the whole
 * Boss / SpawningSystem / renderer pipeline works unchanged. Four of the six
 * signatures needed something the engine didn't have; each is declared the same
 * way Act II declared `gloaming` / `ballast` / `polarity`:
 *
 *  - **`ostinato`** is a pattern (patterns/Ostinato.js) reading the movement
 *    ring buffer this act adds to Player.
 *  - **`understudy`** is a pattern (patterns/Understudy.js) running the
 *    player's real `Weapon` subclasses through a hostile-fire adapter.
 *  - **`levy`**, **`manifold`** and **`verse`** are fight-long def textures,
 *    owned by small systems under systems/act3/ and driven from Game — the same
 *    call Act II made for its own fight-long rules, and for the same reason: a
 *    rule that must hold for the whole fight can't be a pattern the scheduler
 *    picks now and then.
 *
 * Every one of those textures accepts an optional `minPhase`, which is how the
 * Anthem layers all four in as it escalates (its Chorus signature), exactly as
 * the Undersong layered Act II's.
 *
 * PATTERN DENSITY (A7). Counting `createPatterns()` alone, the acts thin out as
 * they get harder — Act I 6–8 a boss, Act II 5–6, Act III 5. That count is the
 * wrong denominator, and the act-level position is that Act III buys its
 * pressure in two currencies Act I does not have:
 *
 *  - **Fight-long textures.** `levy`, `manifold` and `verse` are things the
 *    player must hold in their head for the whole fight, and the Anthem stacks
 *    four of them. Counting a texture as the thing-to-learn it is, every Act
 *    III boss is at 5–8, which is Act I's range.
 *  - **Rotation frequency.** `pickCooldownMultiplier` falls 1.0 (Act I) → 0.8–
 *    0.72 (Act II) → 0.78–0.64 here. A five-pattern rotation at 0.64 puts more
 *    attacks on screen per minute than an eight-pattern one at 1.0; fewer
 *    distinct things, each of them more often.
 *
 * So the individual "this fight would be unreadable denser" arguments below
 * stand, and they are not each an isolated exception — they are the act's
 * design. What was NOT defensible was the floor: the Understudy was at four
 * patterns with a signature that IS one of the four, leaving it the only boss
 * in the game whose thing-to-learn count fell below five. It has a homing
 * volley now, and five is the floor everywhere.
 *
 * `layerFromPhase` runs 2, 2, 1, 1, 1, 0 down this array (A9), one boss ahead
 * of Act II's 2,2,2,1,1,0 because Act III's player starts eight `actStartLevel`
 * levels stronger. See the staircase note in data/bosses.js.
 *
 * BULLET DENSITY (A18). The Verse used to out-shoot every fight in the game,
 * its own finale included. Fixed at the Verse's `Composition` rather than by
 * inflating the Anthem, and the restraint is the point: this act's finale
 * carries most of its pressure in textures a bullet counter cannot see, so
 * raising its bullet rate to win a comparison in the one unit that misreads it
 * would repeat the mistake A8 made in HP. Measured now, bullets/second at
 * phases 0-3: Tithe 6/24/23/30, Manifold 6/26/23/30, Verse 10/30/32/31,
 * Anthem 6/12/19/19.
 */

// The act's own HP staircase (ACT3-BIBLE §9). It opens BELOW the Undersong's
// 34000 and climbs well past it: Act III is a standalone route, so the player
// arrives on whatever build the act's own start grants rather than on one
// carried through everything before it, and the opener is tuned against that
// arrival state. (This used to contrast against "a fully-stacked thirteen-boss
// build" — the appended campaign that phrase means was retired by the act
// split; every act-select start is an arrival now. See acts.js, which made the
// same correction for Act II.) ~1.18x a stage.
//
// The 24 levels `actStartLevel` grants here against Act II's 16 is an artifact
// of roster position (startIndex 13 vs 7), not a tuning decision — flagged in
// acts.js as the thing to settle if either act's opener is ever re-tuned.
export const ACT3_BOSSES = [
  {
    name: 'The Ostinato',
    subtitle: 'Act III · The Anthem',
    // Near-black with a gold accent — the colour of your own trail, lit.
    arenaTheme: { floor: '#0e0b12', line: '#2b2438', accent: '#ffd166' },
    spriteKey: 'ostinato',
    musicTheme: 'ostinato',

    // BOSS-REWORK.md §B4/§B8 — a figure that repeats until it stops being a
    // figure: it holds its station and its wind-ups are metronomic.
    movement: 'anchor',
    telegraphScale: 0.85,
    cardBreak: { fraction: 0.06, cards: ['Ostinato'] },
    script: [
      ['Ostinato', 'Radial Ring'],
      ['Ostinato', 'Bullet Board'],
    ],
    maxHp: 28000,
    moveSpeed: 42,
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.78,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 13, speed: 96 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 14, speed: 104, turnRate: 1.85 } }),
      new Implosion({ minPhase: 1, config: { count: 17, damage: 13, speed: 136 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 13, speed: 64 } }),
      // Signature: your own path, a breath late, hot enough to kill.
      new Ostinato({
        minPhase: 0,
        config: {
          replayDelay: 3.0, shortenPerPhase: 0.55, minDelay: 1.4,
          trailWidth: 18, tickDamage: 9, ghostLead: 0.2, strettoPhase: 3,
        },
      }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 64,
      minionMoveSpeed: 96,
      minionContactDamage: 14,
      minionShotDamage: 7,
      minionShotSpeed: 96,
      minionFireInterval: 1.7,
      // Tracers walk where you walked — the rule as a creature, before the
      // boss makes it the floor.
      songMinionTypes: [{ type: 'tracer', weight: 3 }],
    },
  },
  {
    name: 'The Understudy',
    subtitle: 'Act III · The Anthem',
    arenaTheme: { floor: '#0c0e14', line: '#242a38', accent: '#7fe0a0' },
    spriteKey: 'understudy',
    musicTheme: 'understudy',

    // §B4 — the boss with no kit of its own moves like the thing it copies:
    // it mirrors, and it fires your own loadout back from over there.
    movement: 'mirror',
    cardBreak: { fraction: 0.06, cards: ['Understudy'] },
    script: [
      ['Understudy', 'Radial Ring'],
      ['Understudy', 'Sweeping Laser'],
    ],
    maxHp: 33000,
    moveSpeed: 44,
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.76,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 13, speed: 98 } }),
      new SweepingLaser({ minPhase: 1, config: { tickDamage: 6, tickInterval: 0.14, width: 7, length: 9999, sweepSpeed: 1.5 } }),
      new Implosion({ minPhase: 1, config: { count: 17, damage: 13, speed: 136 } }),
      // A7: the fifth pattern, and homing rather than a board grid on purpose —
      // the mirror's fire is player-shaped (fast, aimed, arriving in bursts),
      // so a slow tracker is the thing it doesn't already cover, and it reads
      // against mirrored fire in a way a full-arena wall would not.
      new HomingShardVolley({ minPhase: 2, config: { damage: 14, speed: 104, turnRate: 1.85 } }),
      // Signature: your build, played back at you.
      //
      // BALANCE NOTE (ACT3-BIBLE §3): this boss's difficulty is authored by the
      // player, so `damageScale` has to be tuned against the STRONGEST fusion
      // build, not the average one. It is the fight's only safety valve, and
      // the reason the Understudy is boss #2 rather than #5 — a mirrored
      // late-game loadout at boss five would be lethal on contact.
      new Understudy({
        minPhase: 0,
        config: {
          mirrorSlots: 3, cadenceMultiplier: 0.55, damageScale: 0.7,
          refreshOnLevelUp: true, curtainCallPhase: 3, curtainCallDuration: 2.0,
        },
      }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 66,
      minionMoveSpeed: 98,
      minionContactDamage: 14,
      minionShotDamage: 7,
      minionShotSpeed: 98,
      minionFireInterval: 1.65,
      songMinionTypes: [{ type: 'mimicMote', weight: 3 }],
    },
  },
  {
    name: 'The Tithe',
    subtitle: 'Act III · The Anthem',
    arenaTheme: { floor: '#100c0a', line: '#33291f', accent: '#d9a441' },
    spriteKey: 'tithe',
    musicTheme: 'tithe',
    // Signature: immune until you pay into the collection ring. See
    // systems/act3/LevySystem.js — including the guard rails, which are
    // load-bearing rather than polish.
    levy: {
      leviesPerPhase: 2,
      windowSeconds: [6, 9, 13],
      ringRadius: 46,
      maxHpCost: 0.1,
      maxHpFloor: 0.4,
      arrearsSeconds: 14,
      refusalHeal: 0.05,
    },

    // §B4 — a tollkeeper comes to collect. It closes and stays closed.
    movement: 'stalk',
    movementConfig: { standoff: 75 },
    cardBreak: { fraction: 0.06, cards: ['Warding Cores'] },
    script: [
      ['Warding Cores', 'Bullet Board'],
      ['Warding Cores', 'Sweeping Laser'],
    ],
    maxHp: 39000,
    moveSpeed: 38,
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.74,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 14, speed: 96 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 15, speed: 106, turnRate: 1.9 } }),
      new BulletBoardGrid({ minPhase: 1, config: { damage: 14, speed: 66 } }),
      new Implosion({ minPhase: 2, config: { count: 18, damage: 14, speed: 138 } }),
      new SweepingLaser({ minPhase: 2, config: { tickDamage: 7, tickInterval: 0.13, width: 7, length: 9999, sweepSpeed: 1.5 } }),
      // BOSS-REWORK.md §B9 — the game's first card you can answer with the
      // fire button. Fitting on the boss whose whole fiction is being made to
      // pay something: here the toll is shots spent on the board.
      new WardingCores({ minPhase: 0, config: {
        count: 4, plantRadius: 78, coreRadius: 7, coreHp: 220,
        holdFor: 1.5, ringCount: 12, ringSpeed: 92, damage: 13,
      } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 68,
      minionMoveSpeed: 96,
      minionContactDamage: 15,
      minionShotDamage: 7,
      minionShotSpeed: 98,
      minionFireInterval: 1.65,
      songMinionTypes: [{ type: 'tollkeeper', weight: 3 }],
    },
  },
  {
    name: 'The Manifold',
    subtitle: 'Act III · The Anthem',
    arenaTheme: { floor: '#0a0d10', line: '#1f2d33', accent: '#66d9ff' },
    spriteKey: 'manifold',
    musicTheme: 'manifold',
    // Signature: a second body, mirroring your movement, sharing your HP. See
    // systems/act3/ManifoldSystem.js — including the accessibility assist,
    // which the bible makes a shipping requirement and which lives in
    // SettingsMenu as "Manifold assist".
    manifold: {
      bodies: 2,
      bodyRadius: 7,
      axisDriftPerPhase: 0.35,
      divisiPhase: 3,
      damageTaken: 1.0,
      damageInterval: 0.4,
      damageRadius: 40,
      damagePerTick: 26,
      assistOffset: 46,
      assistDamageScale: 0.6,
    },

    // §B4 — two bodies, one of them always somewhere else: the primary keeps
    // its distance so the pair spans the arena.
    movement: 'keepAway',
    movementConfig: { preferredRange: 200 },
    script: [
      ['Radial Ring', 'Sweeping Laser'],
      ['Implosion', 'Homing Shard Volley'],
    ],
    maxHp: 46000,
    moveSpeed: 42,
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.72,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 14, speed: 98 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 15, speed: 108, turnRate: 1.9 } }),
      new Implosion({ minPhase: 1, config: { count: 18, damage: 14, speed: 140 } }),
      // Beams read cleanly against two bodies in a way dense bullet walls
      // don't — with the mirror live, a full-arena grid is unreadable.
      new SweepingLaser({ minPhase: 2, config: { tickDamage: 7, tickInterval: 0.13, width: 7, length: 9999, sweepSpeed: 1.5 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 70,
      minionMoveSpeed: 98,
      minionContactDamage: 15,
      minionShotDamage: 8,
      minionShotSpeed: 100,
      minionFireInterval: 1.6,
      songMinionTypes: [{ type: 'twinbound', weight: 3 }],
    },
  },
  {
    name: 'The Verse',
    subtitle: 'Act III · The Anthem',
    arenaTheme: { floor: '#0d0a10', line: '#2a2033', accent: '#ff8ac4' },
    spriteKey: 'verse',
    musicTheme: 'verse',
    // Signature: at each phase transition the player picks which way the boss
    // grows. See systems/act3/VerseSystem.js (the offering + the arena rule)
    // and patterns/Composition.js (what the picks sound like).
    verse: {
      picksPerPhase: 1,
      handSize: 3,
      narrowFraction: 0.62,
      narrowTickDamage: 12,
    },

    // §B4 — the player authors this fight's escalation; the boss holds still
    // and plays what it was handed.
    movement: 'anchor',
    cardBreak: { fraction: 0.06, cards: ['Composition'] },
    script: [
      ['Composition', 'Radial Ring'],
      ['Composition', 'Bullet Board'],
    ],
    maxHp: 54000,
    moveSpeed: 44,
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.7,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 15, speed: 100 } }),
      new Implosion({ minPhase: 1, config: { count: 18, damage: 15, speed: 142 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 14, speed: 66 } }),
      // Signature volley — reads boss.versePicks, so it is literally shaped by
      // what the player wrote.
      //
      // A18: `interval` is 0.8, not 0.5. Composition has `minPhase: 0` and so
      // runs ALONGSIDE the normal rotation rather than in place of it, and at
      // 0.5 it was adding a steady ~14 bullets/second on top of a full one —
      // which made the act's fifth boss the densest fight in the game, ahead
      // of its own finale (`spawning/threatBench.js`: 14/36/37/38 b/s against
      // the Manifold's 6/27/24/30). Slowed rather than shrunk, deliberately:
      // `count` is the volley's SHAPE, and the shape is the whole point of a
      // boss the player writes. A thinner volley would have read as a
      // different attack; a less frequent one reads as the same attack with
      // room to breathe. Now 10/30/29/33.
      new Composition({
        minPhase: 0,
        config: {
          count: 12, denserBonus: 10, interval: 0.8, speed: 92,
          swiftMultiplier: 1.5, damage: 14, turnRate: 1.5, color: '#ff8ac4',
        },
      }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 13,
      minionHp: 72,
      minionMoveSpeed: 100,
      minionContactDamage: 15,
      minionShotDamage: 8,
      minionShotSpeed: 100,
      minionFireInterval: 1.6,
      // No single creature echoes "the player writes the boss", so the Verse
      // gets the act's mixed set instead — by here the act's creatures are
      // vocabulary the player already reads. Same call the Swell made in Act
      // II, down to leaving one of the four out: the twinbound's split-and-
      // rejoin is its own puzzle and reads as a competing rule beside the
      // Verse's, rather than as texture underneath it.
      songMinionTypes: [
        { type: 'tracer', weight: 1 },
        { type: 'mimicMote', weight: 1 },
        { type: 'tollkeeper', weight: 1 },
      ],
    },
  },
  {
    name: 'The Anthem',
    subtitle: 'Act III · Finale',
    isFinalBoss: true,
    hasPulseGlow: true,
    spriteKeyPhase2: 'anthemPhase2',
    // INVERTED, and the only one in the game: Act I climbed to a dying court,
    // Act II descended into black, and Act III ends in full, unbearable
    // daylight — because the song won.
    arenaTheme: { floor: '#f2f0ea', line: '#d8d2c4', accent: '#1a1620' },
    spriteKey: 'anthem',
    musicTheme: 'anthem',
    // SIGNATURE — Chorus: the finale takes up all of Act III's textures, one
    // per phase, the way TheUnmaking and Unsinging recapitulated their acts.
    // The two systems-backed textures gate by phase here; the two pattern-based
    // ones (path replay, build mirror) gate through the normal pattern minPhase
    // below. Both are dialled well under the bosses that introduced them: the
    // composite is the threat, not any one voice.
    levy: {
      minPhase: 2,
      leviesPerPhase: 1,
      windowSeconds: [8, 11, 15],
      ringRadius: 52,
      maxHpCost: 0.08,
      maxHpFloor: 0.4,
      arrearsSeconds: 16,
      refusalHeal: 0.03,
    },
    manifold: {
      minPhase: 3,
      bodies: 2,
      bodyRadius: 7,
      axisDriftPerPhase: 0.25,
      divisiPhase: 99, // no Divisi in the finale; three bodies plus everything else is soup
      damageTaken: 0.8,
      damageInterval: 0.4,
      damageRadius: 40,
      damagePerTick: 26,
      assistOffset: 46,
      assistDamageScale: 0.6,
    },

    // Top of the Act III staircase.
    // §B4/§B8 — the last boss in the game mirrors and reads fast. It never
    // gives an unlayered phase (layerFromPhase 0) and it never gives a slow
    // wind-up either.
    movement: 'mirror',
    telegraphScale: 0.85,
    cardBreak: { fraction: 0.05, cards: ['Ostinato', 'Understudy'] },
    script: [
      ['Ostinato', 'Understudy'],
      ['Understudy', 'Radial Ring'],
      ['Ostinato', 'Implosion'],
    ],
    maxHp: 64000,
    moveSpeed: 46,
    layerFromPhase: 0,
    pickCooldownMultiplier: 0.64,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 15, speed: 100 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 16, speed: 110, turnRate: 1.95 } }),
      new Implosion({ minPhase: 1, config: { count: 19, damage: 15, speed: 144 } }),
      new SweepingLaser({ minPhase: 2, config: { tickDamage: 7, tickInterval: 0.13, width: 7, length: 9999, sweepSpeed: 1.6 } }),
      new Ostinato({
        minPhase: 0,
        config: { replayDelay: 2.6, shortenPerPhase: 0.4, minDelay: 1.5, trailWidth: 16, tickDamage: 10, ghostLead: 0.2, strettoPhase: 3 },
      }),
      new Understudy({
        minPhase: 1,
        config: { mirrorSlots: 2, cadenceMultiplier: 0.5, damageScale: 0.6, refreshOnLevelUp: true, curtainCallPhase: 3, curtainCallDuration: 1.6 },
      }),
    ],
    // Phase 2 "true form": Everyone At Once.
    createPhase2Pattern: () => new EveryoneAtOnce({
      config: {
        ostinato: { replayDelay: 2.2, shortenPerPhase: 0, minDelay: 1.6, trailWidth: 15, tickDamage: 8, ghostLead: 0.2, strettoPhase: 99 },
        understudy: { mirrorSlots: 2, cadenceMultiplier: 0.45, damageScale: 0.5, refreshOnLevelUp: true },
        ringInterval: 1.5, ringCount: 16, ringSpeed: 82, ringDamage: 13, color: '#1a1620',
      },
    }),
    // Desperation: survive the performance.
    createDesperationPattern: () => new TheLastVerse({
      config: {
        ostinato: { replayDelay: 1.8, shortenPerPhase: 0, minDelay: 1.4, trailWidth: 17, tickDamage: 12, ghostLead: 0.25 },
        understudy: { mirrorSlots: 3, cadenceMultiplier: 0.7, damageScale: 0.65, refreshOnLevelUp: false, curtainCallDuration: 2.0 },
      },
    }),
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 13,
      minionHp: 74,
      minionMoveSpeed: 102,
      minionContactDamage: 16,
      minionShotDamage: 8,
      minionShotSpeed: 102,
      minionFireInterval: 1.55,
      // The finale recapitulates in its waves the way its patterns do.
      songMinionTypes: [
        { type: 'tracer', weight: 2 },
        { type: 'mimicMote', weight: 2 },
        { type: 'tollkeeper', weight: 2 },
        { type: 'twinbound', weight: 2 },
      ],
    },
  },
];

/**
 * Mirrors createDeepWarden in data/act2Bosses.js: a warden is its parent boss
 * with a shorter health bar and exactly one always-pickable pattern —
 * `layerFromPhase: 99` so nothing stacks, no phase-2/desperation, and none of
 * the parent's fight-long textures, so it stays a bounded gate rather than a
 * second full fight.
 */
function createHeraldOfTheSong(bossDef, { name, maxHp, createPattern, overrides = {} }) {
  return {
    ...bossDef,
    ...overrides,
    name,
    maxHp,
    layerFromPhase: 99,
    pickCooldownMultiplier: 1,
    isMiniboss: true,
    createPatterns: () => [createPattern()],
    createPhase2Pattern: undefined,
    createDesperationPattern: undefined,
    isFinalBoss: false,
    minibosses: undefined,
    // A warden inherits no fight-long texture: a levy ring, a second body, or a
    // card hand in a two-minute gate fight would each be a whole boss's worth
    // of rule with none of its framing.
    levy: undefined,
    manifold: undefined,
    verse: undefined,
  };
}

const SONG_SOURCES = {
  ostinato: ACT3_BOSSES.find((b) => b.spriteKey === 'ostinato'),
  understudy: ACT3_BOSSES.find((b) => b.spriteKey === 'understudy'),
  verse: ACT3_BOSSES.find((b) => b.spriteKey === 'verse'),
};

/**
 * The Heralds of the New Song (ACT3-BIBLE §4) — three wardens gating the
 * Anthem, mirroring Act II's three. Each reuses an Act III boss's sprite and
 * one signature-adjacent texture, held STATIC: the reduced, unescalating
 * version of a rule the player already met. That's what a warden is for — it
 * tests what the act taught, it doesn't teach anything new right before the
 * finale does.
 *
 * The Manifold deliberately has no herald: a second body is too disorienting
 * for a gate fight, and the assist toggle needs the boss's full framing.
 */
export const ACT3_MINIBOSSES = [
  createHeraldOfTheSong(SONG_SOURCES.ostinato, {
    name: 'Ostinato-Trace',
    maxHp: 12000,
    // One replayed path at a long, FIXED delay — shortenPerPhase 0, no Stretto.
    createPattern: () => new Ostinato({
      minPhase: 0,
      config: { replayDelay: 3.2, shortenPerPhase: 0, minDelay: 3.2, trailWidth: 18, tickDamage: 12, ghostLead: 0.2, strettoPhase: 99 },
    }),
  }),
  createHeraldOfTheSong(SONG_SOURCES.understudy, {
    name: 'Understudy-Shade',
    maxHp: 12000,
    // One weapon slot only, so a build's single strongest gun is the whole
    // fight rather than the whole loadout at once.
    createPattern: () => new Understudy({
      minPhase: 0,
      config: { mirrorSlots: 1, cadenceMultiplier: 0.6, damageScale: 0.7, refreshOnLevelUp: false, curtainCallPhase: 99 },
    }),
  }),
  createHeraldOfTheSong(SONG_SOURCES.verse, {
    name: 'Verse-Fragment',
    maxHp: 12000,
    // One pick, taken automatically — the fragment writes its own line and the
    // player fights a verse they never chose. `tracking` rather than `denser`:
    // a warden should test reading a pattern, not surviving a wall.
    createPattern: () => new Composition({
      minPhase: 0,
      config: {
        count: 14, denserBonus: 0, interval: 0.55, speed: 96, swiftMultiplier: 1,
        damage: 15, turnRate: 1.5, color: '#ff8ac4', forcedPicks: ['tracking'],
      },
    }),
  }),
];

/**
 * Per-boss run-end rewards for the Act III slots (ACT3-BIBLE §9). Continues the
 * Act II curves, but the rate-parity pass matters more here than anywhere else
 * in the game: Act III is the FIRST rewarded act-select route, and a six-boss
 * rewarded run must not out-earn the thirteen-boss campaign *per minute*, or
 * the campaign becomes the slow way to earn.
 *
 * The benchmark used: Act II's six bosses pay 2220 Echoes / 102 Shards; the
 * full campaign pays that plus Act I's 915 / 34, over roughly twice the run
 * time. Act III's totals below (2280 / 104) sit level with Act II's six-boss
 * stretch rather than above it — so the branch pays like the half-campaign it
 * is, and clearing Act I → Act II remains the higher total for the higher time.
 *
 * The Anthem's slot spikes the way both earlier finales do (Currency.js:8-11):
 * +100 Echoes and +10 Shards against the +40 / +2 of every prior step, so the
 * last kill reads as the climax rather than one more increment. The spike is
 * paid for out of the five slots in front of it — the totals above are the
 * rate-parity budget and did not move.
 */
export const ACT3_ECHO_BONUS = [270, 310, 350, 390, 430, 530];
export const ACT3_SHARDS = [11, 13, 15, 17, 19, 29];

// Attached after the fact rather than inline, for the same reason Act II does
// it: ACT3_MINIBOSSES is derived FROM ACT3_BOSSES, so referencing it inside the
// array that defines it would be a cycle.
const ANTHEM = ACT3_BOSSES.find((b) => b.spriteKey === 'anthem');
ANTHEM.minibosses = ACT3_MINIBOSSES;
ANTHEM.minibossLoreKey = 'songHeralds';
